import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    AppointmentsCalendarView,
    getTodayIso,
    isDocEmpty,
    isNotCanceled,
    needsChartNote,
    formatShortDate,
    type AppointmentsCalendarComposer,
    type CalendarAppointment
} from '@odo/shared-ui';
import Composer from './Composer';
import ConfirmCancelModal from './ConfirmCancelModal';
import OpenAppointmentsPdfButton from './OpenAppointmentsPdfButton';
import { selectUpcoming } from './selectUpcoming';
import StageChip from './StageChip';
import { Avatar } from '../Avatar';
import { emptyDraft, EMPTY_CHART_NOTE_DOC, type AppointmentDraft } from './types';
import {
    useAppointmentsMine,
    useCancelAppointment,
    useCreateAppointment,
    useUpdateAppointment
} from '../../hooks/useAppointments';
import { usePatientList } from '../../providers/patient/PatientsListProvider';
import { useToast } from '../../providers/ToastProvider';
import type { Appointment, AppointmentNoteInput } from '../../services/appointments';
import { buildAppointmentNoteTitle } from '../../utils/appointments/noteDoc';

interface AppointmentsModalProps {
    onClose: () => void;
}

// Which appointment (if any) is composed lives in `?appointment=<id|new>`
// (push-on-open, replace-on-close, like billing's INVOICE_QUERY_PARAM); the draft's content itself is never serialized.
export const APPOINTMENT_QUERY_PARAM = 'appointment';
export const NEW_APPOINTMENT_PARAM = 'new';
/** Patient to preselect for a NEW appointment (the patients table's "+"),
 *  only meaningful alongside `?appointment=new`. */
export const APPOINTMENT_PATIENT_QUERY_PARAM = 'appointmentPatient';

function draftFromAppointment(appointment: Appointment): AppointmentDraft {
    return {
        id: appointment.id,
        patientId: appointment.patientId,
        date: appointment.date,
        time: appointment.time ?? '',
        reason: appointment.reason ?? '',
        chartNote: EMPTY_CHART_NOTE_DOC,
        chartNoteTitle: ''
    };
}

const AppointmentsModal: React.FC<AppointmentsModalProps> = ({ onClose }) => {
    const todayIso = useMemo(() => getTodayIso(), []);
    const { appointments, isLoading, isError, refetch } = useAppointmentsMine();
    const { patients } = usePatientList();
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const appointmentParam = searchParams.get(APPOINTMENT_QUERY_PARAM);
    const appointmentPatientParam = searchParams.get(APPOINTMENT_PATIENT_QUERY_PARAM);

    const createMutation = useCreateAppointment();
    const updateMutation = useUpdateAppointment();
    const cancelMutation = useCancelAppointment();

    const [search, setSearch] = useState('');
    const [draft, setDraft] = useState<AppointmentDraft | null>(null);
    const [confirmTarget, setConfirmTarget] = useState<Appointment | null>(null);
    const [focusChartNoteOnOpen, setFocusChartNoteOnOpen] = useState(false);

    const stageByPatientId = useMemo(() => {
        const map = new Map<string, string | null>();
        patients.forEach((patient) => map.set(patient.id, patient.treatmentStatus));
        return map;
    }, [patients]);

    const activeAppointments = useMemo(() => appointments.filter(isNotCanceled), [appointments]);
    const needsNoteCount = useMemo(
        () => activeAppointments.filter((a) => needsChartNote(a, todayIso)).length,
        [activeAppointments, todayIso]
    );
    // Search-aware, so the count always describes exactly what the PDF would print.
    const upcomingCount = useMemo(
        () => selectUpcoming(activeAppointments, search, todayIso).length,
        [activeAppointments, search, todayIso]
    );

    // Pushes a new history entry opening the composer for `value` (an
    // appointment id, or NEW_APPOINTMENT_PARAM).
    const openAppointmentParam = (value: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(APPOINTMENT_QUERY_PARAM, value);
            // Only the external "+" deep-link carries a preselected patient; drop it so a stale id doesn't follow the user around.
            next.delete(APPOINTMENT_PATIENT_QUERY_PARAM);
            return next;
        });
    };

    // Replaces (not pushes) the `appointment` param: the single exit path
    // for Discard/Escape/a successful Save, so leaving never stacks history.
    const closeComposer = () => {
        setDraft(null);
        setFocusChartNoteOnOpen(false);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(APPOINTMENT_QUERY_PARAM);
                next.delete(APPOINTMENT_PATIENT_QUERY_PARAM);
                return next;
            },
            { replace: true }
        );
    };

    // Lets the reconciliation effect below tell "already in sync" (the
    // handlers above move both together) from "URL changed independently".
    const draftIdentity = draft ? (draft.id ?? NEW_APPOINTMENT_PARAM) : null;

    // The URL is the source of truth for which appointment is composed;
    // reconciles local `draft` to it on mount and on Back/Forward.
    useEffect(() => {
        if (isLoading || isError) return;
        if (appointmentParam === draftIdentity) return;

        if (!appointmentParam) {
            setDraft(null);
            setFocusChartNoteOnOpen(false);
            return;
        }
        if (appointmentParam === NEW_APPOINTMENT_PARAM) {
            setFocusChartNoteOnOpen(false);
            // Preselects the patient when the opener named one (the table's "+"); everything else stays the blank Add-button form.
            setDraft({ ...emptyDraft(todayIso), patientId: appointmentPatientParam });
            return;
        }
        const found = activeAppointments.find((a) => a.id === appointmentParam);
        if (found) {
            setFocusChartNoteOnOpen(false);
            setDraft(draftFromAppointment(found));
            return;
        }
        // Deleted, canceled, wrong practice, or a stale link: there's no
        // per-id lookup, so "not in the loaded list" is the 404 signal.
        showToast('Appointment not found', 'error');
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(APPOINTMENT_QUERY_PARAM);
                return next;
            },
            { replace: true }
        );
    }, [
        appointmentParam,
        draftIdentity,
        activeAppointments,
        isLoading,
        isError,
        todayIso,
        showToast,
        setSearchParams
    ]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (confirmTarget) {
                setConfirmTarget(null);
            } else if (draft) {
                closeComposer();
            } else {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // closeComposer omitted: fresh identity every render, not part of what this effect reacts to.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmTarget, draft, onClose]);

    const handleAddAppointment = (prefillDate: string) => {
        setFocusChartNoteOnOpen(false);
        setDraft(emptyDraft(prefillDate));
        openAppointmentParam(NEW_APPOINTMENT_PARAM);
    };

    const handleEditAppointment = (appointment: CalendarAppointment) => {
        setFocusChartNoteOnOpen(false);
        setDraft(draftFromAppointment(appointment));
        openAppointmentParam(appointment.id);
    };

    // Same edit composer as a row click, but signals ChartNotesField to focus/scroll to the textarea on mount.
    const handleAddNoteFromRow = (appointment: CalendarAppointment) => {
        setFocusChartNoteOnOpen(true);
        setDraft(draftFromAppointment(appointment));
        openAppointmentParam(appointment.id);
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;
    const editingAppointment = draft?.id
        ? (appointments.find((a) => a.id === draft.id) ?? null)
        : null;

    const handleSave = async () => {
        if (!draft || !draft.patientId) return;
        const trimmedReason = draft.reason.trim();
        const trimmedTime = draft.time.trim();
        // isDocEmpty (not a length check), matching ChartNotesField; Composer blocks Save on a doc with no text.
        const chartNoteIsEmpty = isDocEmpty(draft.chartNote);
        const hadLinkedNote = !!editingAppointment?.noteId;
        // Tri-state: non-empty doc saves; empty doc on a previously-noted
        // appointment sends `null` to clear it; otherwise a no-op omit. An empty title falls back to the derived one.
        const trimmedTitle = draft.chartNoteTitle.trim();
        const noteTitle =
            trimmedTitle || buildAppointmentNoteTitle(trimmedReason, formatShortDate(draft.date));
        const note: AppointmentNoteInput | null | undefined = chartNoteIsEmpty
            ? hadLinkedNote
                ? null
                : undefined
            : {
                  title: noteTitle,
                  bodyDoc: draft.chartNote
              };

        try {
            if (draft.id) {
                await updateMutation.mutateAsync({
                    patientId: draft.patientId,
                    appointmentId: draft.id,
                    date: draft.date,
                    time: trimmedTime || null,
                    reason: trimmedReason,
                    ...(note !== undefined && { note })
                });
            } else {
                await createMutation.mutateAsync({
                    patientId: draft.patientId,
                    date: draft.date,
                    ...(trimmedTime && { time: trimmedTime }),
                    reason: trimmedReason,
                    ...(note !== undefined && { note })
                });
            }
            closeComposer();
        } catch {
            // The mutation's onError already surfaced a toast; keep the
            // composer open so the user doesn't lose their input.
        }
    };

    const handleCancelFromRow = (appointment: CalendarAppointment) => {
        setConfirmTarget(appointment);
    };

    const handleConfirmCancel = async () => {
        if (!confirmTarget) return;
        try {
            await cancelMutation.mutateAsync({
                patientId: confirmTarget.patientId,
                appointmentId: confirmTarget.id
            });
            setConfirmTarget(null);
        } catch {
            // Toast already shown by the mutation's onError.
        }
    };

    const composer: AppointmentsCalendarComposer | undefined = draft
        ? {
              label: draft.id ? 'Edit appointment' : 'New appointment',
              onClose: closeComposer,
              highlightDate: draft.date,
              content: (
                  <Composer
                      draft={draft}
                      editingAppointment={editingAppointment}
                      appointments={activeAppointments}
                      patients={patients}
                      isSaving={isSaving}
                      todayIso={todayIso}
                      onChange={setDraft}
                      onSave={() => void handleSave()}
                      onClose={closeComposer}
                      autoFocusChartNote={focusChartNoteOnOpen}
                  />
              )
          }
        : undefined;

    return (
        // Deliberately no backdrop-click close: a half-written appointment is easy to lose. Closing is explicit (X or Escape).
        <div className="fixed inset-0 z-40 flex items-center justify-center p-6 bg-gray-900/30">
            <div className="w-full max-w-[1240px] h-[85vh] max-h-[860px] flex flex-col rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between gap-4 px-5 h-[60px] border-b border-gray-200 shrink-0">
                    <div className="flex items-center gap-2.5 shrink-0">
                        <span className="text-gray-400">
                            <CalendarDaysIcon className="h-[18px] w-[18px]" />
                        </span>
                        <h2 className="text-[15px] font-semibold text-gray-800">Appointments</h2>
                        <span className="text-[13px] text-gray-400">
                            {upcomingCount} upcoming
                            {needsNoteCount > 0 && (
                                <>
                                    {' '}
                                    ·{' '}
                                    <span className="text-amber-700 font-medium">
                                        {needsNoteCount} without chart notes
                                    </span>
                                </>
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative w-60">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <MagnifyingGlassIcon className="h-[15px] w-[15px]" />
                            </span>
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by patient name"
                                className="w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-[13px] placeholder-gray-400 focus:outline-none focus:border-gray-400"
                            />
                        </div>
                        <OpenAppointmentsPdfButton
                            appointments={activeAppointments}
                            search={search}
                            todayIso={todayIso}
                        />
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="h-9 w-9 grid place-items-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-800"
                        >
                            <XMarkIcon className="h-[17px] w-[17px]" />
                        </button>
                    </div>
                </div>

                <AppointmentsCalendarView
                    appointments={activeAppointments}
                    isLoading={isLoading}
                    isError={isError}
                    onRetry={() => void refetch()}
                    todayIso={todayIso}
                    readOnly={false}
                    search={search}
                    onSearchChange={setSearch}
                    renderAvatar={(appointment) => (
                        <Avatar
                            user={{ name: appointment.patientName, email: appointment.patientId }}
                            imageURL={appointment.profilePictureUrl ?? undefined}
                        />
                    )}
                    renderStage={(appointment) => (
                        <StageChip stage={stageByPatientId.get(appointment.patientId) ?? null} />
                    )}
                    onSelectAppointment={handleEditAppointment}
                    onAddAppointment={handleAddAppointment}
                    onCancelAppointment={handleCancelFromRow}
                    onAddChartNote={handleAddNoteFromRow}
                    composer={composer}
                />
            </div>

            {confirmTarget && (
                <ConfirmCancelModal
                    appointment={confirmTarget}
                    isCanceling={cancelMutation.isPending}
                    onClose={() => setConfirmTarget(null)}
                    onConfirm={() => void handleConfirmCancel()}
                />
            )}
        </div>
    );
};

export default AppointmentsModal;
